import React, { useMemo, useState } from "react";
import TreeHierachy from "../../../../components/treehierarchy/TreeHierarchy";
import { demoAtsDataset } from "./TreeHierarchyList/data";
import {
  normalizeRange,
  parseToDate,
} from "../../../../components/treehierarchy/utils/dateRange";
import dayjs from "dayjs";

const TreeHierarchyList = () => {
  const [duration, setDuration] = useState({
    from: dayjs().toDate(),
    to: dayjs().toDate(),
  });

  const recruiterById = useMemo(() => {
    const m = new Map();
    (demoAtsDataset?.recruiters || []).forEach((r) => {
      if (r?.id == null) return;
      m.set(String(r.id), r?.name ?? String(r.id));
    });
    return m;
  }, []);

  const stageNames = useMemo(
    () => (demoAtsDataset?.stages || []).map((s) => s?.name).filter(Boolean),
    [],
  );

  const timelinesByJobStage = useMemo(() => {
    const internalStageName = stageNames?.[0] ?? "Internal Submission";
    const raw = new Map();

    (demoAtsDataset?.events || []).forEach((evt) => {
      if (!evt?.jobId || !evt?.stage || !evt?.candidateId) return;
      if (
        evt.type !== "approved" &&
        evt.type !== "rejected" &&
        evt.type !== "entered"
      )
        return;
      if (evt.type === "entered" && evt.stage !== internalStageName) return;

      const dayKey = dayjs(evt.at).format("YYYY-MM-DD");
      if (!raw.has(evt.jobId)) raw.set(evt.jobId, new Map());
      const stagesMap = raw.get(evt.jobId);
      if (!stagesMap.has(evt.stage)) stagesMap.set(evt.stage, new Map());
      const datesMap = stagesMap.get(evt.stage);
      if (!datesMap.has(dayKey)) {
        datesMap.set(dayKey, {
          entered: new Set(),
          approved: new Set(),
          rejected: new Set(),
        });
      }
      const bucket = datesMap.get(dayKey);
      if (evt.type === "entered") bucket.entered.add(evt.candidateId);
      if (evt.type === "approved") bucket.approved.add(evt.candidateId);
      if (evt.type === "rejected") bucket.rejected.add(evt.candidateId);
    });

    const out = new Map();
    raw.forEach((stagesMap, jobId) => {
      const byStage = new Map();
      stagesMap.forEach((datesMap, stageName) => {
        const timeline = Array.from(datesMap.entries())
          .map(([dk, v]) => ({
            date: dayjs(dk).startOf("day").toDate(),
            entered: Array.from(v?.entered || []),
            approved: Array.from(v?.approved || []),
            rejected: Array.from(v?.rejected || []),
          }))
          .sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );
        byStage.set(stageName, timeline);
      });
      out.set(jobId, byStage);
    });

    return out;
  }, [stageNames]);

  const filterdData = useMemo(() => {
    const normalizedRange = normalizeRange(duration);
    const startMs = normalizedRange?.startMs ?? null;
    const endMs = normalizedRange?.endMs ?? null;

    const jobsById = {};
    const byDate = new Map();

    (demoAtsDataset?.jobs || []).forEach((job) => {
      const openDate = parseToDate(job?.jobOpenDate);
      if (!openDate) return;
      const openMs = openDate.getTime();
      if (startMs != null && openMs < startMs) return;
      if (endMs != null && openMs > endMs) return;

      const jobId = job?.jobId;
      if (!jobId) return;

      const stages = stageNames.map((name) => ({
        stage: name,
        dates: timelinesByJobStage.get(jobId)?.get(name) || [],
      }));

      jobsById[jobId] = { ...job, stages };

      const dateKey = dayjs(openDate).format("YYYY-MM-DD");
      if (!byDate.has(dateKey)) byDate.set(dateKey, new Map());
      const itemsMap = byDate.get(dateKey);

      const employeeId = job?.bdmId ?? "";
      const companyId =
        job?.companyId ?? `comp_${job?.companyName || "unknown"}`;
      const itemKey = `${employeeId}|${companyId}`;

      if (!itemsMap.has(itemKey)) {
        itemsMap.set(itemKey, {
          employeeId,
          employeeName:
            recruiterById.get(String(employeeId)) ?? String(employeeId),
          companyId,
          companyName: job?.companyName ?? String(companyId),
          jobIds: [],
        });
      }

      itemsMap.get(itemKey).jobIds.push(jobId);
    });

    const dates = Array.from(byDate.entries())
      .map(([dateKey, itemsMap]) => ({
        date: dayjs(dateKey).startOf("day").toDate(),
        items: Array.from(itemsMap.values()).map((it) => ({
          ...it,
          jobIds: Array.from(new Set(it.jobIds)),
        })),
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    dates.forEach((d) => {
      d.items.sort((a, b) => {
        const an = String(a.employeeName || "");
        const bn = String(b.employeeName || "");
        if (an !== bn) return an.localeCompare(bn);
        return String(a.companyName || "").localeCompare(
          String(b.companyName || ""),
        );
      });
    });

    return { dates, jobs: jobsById };
  }, [duration, recruiterById, stageNames, timelinesByJobStage]);

  // console.log("demoAtsDataset", demoAtsDataset);
  // console.log("filterdData", filterdData);

  return (
    <div>
      <TreeHierachy
        data={filterdData}
        duration={duration}
        onDurationChange={setDuration}
      />
    </div>
  );
};

export default TreeHierarchyList;
